import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import DateTimeDisplay from './DateTimeDisplay';
import VerifiedBankAccountFields from './VerifiedBankAccountFields';

const TeacherManagement = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [feedbackRecords, setFeedbackRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('teachers');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedTeacherCards, setExpandedTeacherCards] = useState(new Set());

  // Form states
  const [newTeacher, setNewTeacher] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    qualification: '',
    specialization: '',
    experience: '',
    hireDate: '',
    employmentStatus: 'Full-time',
    baseSalary: '',
    allowances: '',
    deductions: '',
    bankAccount: {
      accountName: '',
      bankName: '',
      bankCode: '',
      bankId: '',
      accountNumber: '',
      verified: false,
      verifiedAt: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  const [editTeacher, setEditTeacher] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    qualification: '',
    specialization: '',
    experience: '',
    hireDate: '',
    employmentStatus: 'Full-time',
    baseSalary: '',
    allowances: '',
    deductions: '',
    profilePicture: '',
    bankAccount: {
      accountName: '',
      bankName: '',
      bankCode: '',
      bankId: '',
      accountNumber: '',
      verified: false,
      verifiedAt: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    checkInTime: '08:00',
    checkOutTime: '16:00',
    notes: ''
  });

  const [salaryForm, setSalaryForm] = useState({
    month: new Date().toISOString().slice(0, 7),
    baseSalary: '',
    allowances: {
      housing: '',
      transport: '',
      medical: ''
    },
    deductions: {
      tax: '',
      insurance: ''
    },
    paymentStatus: 'pending',
    paymentDate: '',
    paymentMethod: 'bank_transfer',
    notes: ''
  });

  const [feedbackForm, setFeedbackForm] = useState({
    type: 'performance_feedback',
    title: '',
    message: '',
    priority: 'normal',
    requestDetails: {
      startDate: '',
      endDate: '',
      reason: '',
      item: '',
      justification: ''
    }
  });

  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Check if admin is logged in
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadTeachers(),
          loadClasses(),
          loadSubjects(),
          loadAttendanceRecords(),
          loadSalaryRecords(),
          loadFeedbackRecords()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const loadTeachers = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      console.log('Loading teachers - Admin user:', adminUser);
      console.log('Admin email header:', adminUser.email);

      const response = await fetch(`${API_ENDPOINTS.TEACHERS}`, {
        headers: {
          'admin-email': adminUser.email
        }
      });

      console.log('API Response status:', response.status);
      console.log('API Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Teachers data received:', data);
        setTeachers(data);
      } else {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        alert(`Failed to load teachers: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Network error loading teachers:', error);
      alert('Network error: Could not connect to server. Make sure backend is running.');
    }
  };

  const loadClasses = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.CLASSES}`);
      if (response.ok) {
        const data = await response.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadSubjects = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SUBJECTS}`);
      if (response.ok) {
        const data = await response.json();
        setSubjects(data);
      }
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const loadAttendanceRecords = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/attendance`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAttendanceRecords(data);
      }
    } catch (error) {
      console.error('Error loading attendance records:', error);
    }
  };

  const loadSalaryRecords = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/salaries/all`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSalaryRecords(data);
      }
    } catch (error) {
      console.error('Error loading salary records:', error);
    }
  };

  const loadFeedbackRecords = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/feedback/all`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFeedbackRecords(data);
      }
    } catch (error) {
      console.error('Error loading feedback records:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Copied to clipboard!');
    });
  };

  const isBankAccountReady = (bankAccount = {}) => {
    const hasAnyBankDetail = Boolean(bankAccount.accountNumber || bankAccount.bankCode || bankAccount.bankName || bankAccount.accountName);
    if (!hasAnyBankDetail) return true;
    return Boolean(
      bankAccount.verified &&
      bankAccount.bankCode &&
      bankAccount.bankName &&
      bankAccount.accountNumber &&
      bankAccount.accountName
    );
  };

  const handleEditTeacher = async (e) => {
    e.preventDefault();

    try {
      if (!isBankAccountReady(editTeacher.bankAccount)) {
        alert('Please select the bank and verify the account number before saving bank details.');
        return;
      }

      const teacherPayload = {
        firstName: editTeacher.firstName || '',
        lastName: editTeacher.lastName || '',
        email: editTeacher.email || '',
        phone: editTeacher.phone || '',
        address: editTeacher.address || '',
        dateOfBirth: editTeacher.dateOfBirth || '',
        gender: editTeacher.gender || '',
        qualification: editTeacher.qualification || '',
        specialization: editTeacher.specialization || '',
        experience: parseInt(editTeacher.experience) || 0,
        hireDate: editTeacher.hireDate || '',
        employmentStatus: editTeacher.employmentStatus || '',
        baseSalary: parseFloat(editTeacher.baseSalary) || 0,
        allowances: parseFloat(editTeacher.allowances) || 0,
        deductions: parseFloat(editTeacher.deductions) || 0,
        bankAccount: {
          accountName: editTeacher.bankAccount?.accountName || '',
          bankName: editTeacher.bankAccount?.bankName || '',
          bankCode: editTeacher.bankAccount?.bankCode || '',
          bankId: editTeacher.bankAccount?.bankId || '',
          accountNumber: editTeacher.bankAccount?.accountNumber || '',
          verified: Boolean(editTeacher.bankAccount?.verified),
          verifiedAt: editTeacher.bankAccount?.verifiedAt || ''
        },
        emergencyContact: editTeacher.emergencyContact || {
          name: '',
          relationship: '',
          phone: ''
        }
      };

      if (profilePictureFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('profilePicture', profilePictureFile);
        const uploadResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${editTeacher.id}/profile-picture`, {
          method: 'PUT',
          body: formDataUpload
        });

        if (!uploadResponse.ok) {
          const error = await uploadResponse.json();
          alert(error.error || 'Failed to update teacher profile picture');
          return;
        }
      }

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${editTeacher.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(teacherPayload)
      });

      if (response.ok) {
        alert('Teacher profile updated successfully!');
        setShowEditModal(false);
        setProfilePictureFile(null);
        loadTeachers();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update teacher');
      }
    } catch (error) {
      console.error('Error updating teacher:', error);
      alert('Failed to update teacher');
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();

    try {
      if (!isBankAccountReady(newTeacher.bankAccount)) {
        alert('Please select the bank and verify the account number before saving bank details.');
        return;
      }

      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const teacherData = {
        ...newTeacher,
        experience: parseInt(newTeacher.experience) || 0,
        baseSalary: parseFloat(newTeacher.baseSalary) || 0,
        allowances: parseFloat(newTeacher.allowances) || 0,
        deductions: parseFloat(newTeacher.deductions) || 0
      };

      const response = await fetch(`${API_ENDPOINTS.TEACHERS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(teacherData)
      });

      if (response.ok) {
        const result = await response.json();

        // Show login credentials to admin
        const credentials = result.loginCredentials;
        alert(`Teacher added successfully!\n\nLogin Credentials:\nEmail: ${credentials.email}\nPassword: ${credentials.password}\n\n${credentials.note}\n\nPlease provide these credentials to the teacher.`);

        setShowAddModal(false);
        setNewTeacher({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          dateOfBirth: '',
          gender: '',
          qualification: '',
          specialization: '',
          experience: '',
          hireDate: '',
          employmentStatus: 'Full-time',
          baseSalary: '',
          allowances: '',
          deductions: '',
          bankAccount: {
            accountName: '',
            bankName: '',
            bankCode: '',
            bankId: '',
            accountNumber: '',
            verified: false,
            verifiedAt: ''
          },
          emergencyContact: {
            name: '',
            relationship: '',
            phone: ''
          }
        });
        loadTeachers();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add teacher');
      }
    } catch (error) {
      console.error('Error adding teacher:', error);
      alert('Failed to add teacher');
    }
  };

  const handleAssignClass = async (teacherId, className) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacherId}/assign-class`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({ className })
      });

      if (response.ok) {
        alert('Teacher assigned to class successfully!');
        loadTeachers();
      } else {
        alert('Failed to assign teacher to class');
      }
    } catch (error) {
      console.error('Error assigning teacher to class:', error);
      alert('Failed to assign teacher to class');
    }
  };

  const handleAssignSubject = async (teacherId, subjectName) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${teacherId}/assign-subject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({ subjectName })
      });

      if (response.ok) {
        alert('Teacher assigned to subject successfully!');
        loadTeachers();
      } else {
        alert('Failed to assign teacher to subject');
      }
    } catch (error) {
      console.error('Error assigning teacher to subject:', error);
      alert('Failed to assign teacher to subject');
    }
  };

  const handleMarkAttendance = async (e) => {
    e.preventDefault();

    if (!selectedTeacher) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${selectedTeacher.id}/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(attendanceForm)
      });

      if (response.ok) {
        alert('Attendance marked successfully!');
        setShowAttendanceModal(false);
        setAttendanceForm({
          date: new Date().toISOString().split('T')[0],
          status: 'present',
          checkInTime: '08:00',
          checkOutTime: '16:00',
          notes: ''
        });
        loadAttendanceRecords(); // Refresh attendance data
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  const handleAddSalary = async (e) => {
    e.preventDefault();

    if (!selectedTeacher) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const salaryData = {
        ...salaryForm,
        baseSalary: parseFloat(salaryForm.baseSalary) || 0,
        allowances: {
          housing: parseFloat(salaryForm.allowances.housing) || 0,
          transport: parseFloat(salaryForm.allowances.transport) || 0,
          medical: parseFloat(salaryForm.allowances.medical) || 0
        },
        deductions: {
          tax: parseFloat(salaryForm.deductions.tax) || 0,
          insurance: parseFloat(salaryForm.deductions.insurance) || 0
        }
      };

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${selectedTeacher.id}/salaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(salaryData)
      });

      if (response.ok) {
        alert('Salary record added successfully!');
        setShowSalaryModal(false);
        setSalaryForm({
          month: new Date().toISOString().slice(0, 7),
          baseSalary: '',
          allowances: { housing: '', transport: '', medical: '' },
          deductions: { tax: '', insurance: '' },
          paymentStatus: 'pending',
          paymentDate: '',
          paymentMethod: 'bank_transfer',
          notes: ''
        });
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add salary record');
      }
    } catch (error) {
      console.error('Error adding salary record:', error);
      alert('Failed to add salary record');
    }
  };

  const handleResetPassword = async (teacher) => {
    if (!window.confirm(`Are you sure you want to reset ${teacher.firstName} ${teacher.lastName}'s passwordLoading They will need to change it on next login.`)) {
      return;
    }

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({
          email: teacher.email,
          newPassword: 'teacher123'
        })
      });

      if (response.ok) {
        alert(`Password reset successfully! ${teacher.firstName} ${teacher.lastName} can now login with:\nEmail: ${teacher.email}\nPassword: teacher123`);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacherLoading This action cannot be undone.')) {
      return;
    }

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.TEACHERS}/${teacherId}`, {
        method: 'DELETE',
        headers: {
          'admin-email': adminUser.email
        }
      });

      if (response.ok) {
        alert('Teacher deleted successfully!');
        loadTeachers(); // Refresh the teacher list
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete teacher');
      }
    } catch (error) {
      console.error('Error deleting teacher:', error);
      alert('Failed to delete teacher');
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();

    if (!selectedTeacher) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const feedbackData = {
        ...feedbackForm,
        submittedBy: adminUser.id,
        submittedByName: adminUser.name,
        submittedByRole: 'admin'
      };

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/${selectedTeacher.id}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(feedbackData)
      });

      if (response.ok) {
        alert('Feedback/request submitted successfully!');
        setShowFeedbackModal(false);
        setFeedbackForm({
          type: 'performance_feedback',
          title: '',
          message: '',
          priority: 'normal',
          requestDetails: {
            startDate: '',
            endDate: '',
            reason: '',
            item: '',
            justification: ''
          }
        });
        loadFeedbackRecords();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback');
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, status, comments) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/feedback/${feedbackId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({ status, adminComments: comments })
      });

      if (response.ok) {
        alert('Feedback status updated successfully!');
        loadFeedbackRecords();
      } else {
        alert('Failed to update feedback status');
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
      alert('Failed to update feedback status');
    }
  };

  const getRequestTypeLabel = (type) => {
    const labels = {
      performance_feedback: 'Performance Feedback',
      leave_request: 'Leave Request',
      equipment_request: 'Equipment Request',
      maintenance_request: 'Maintenance Request',
      other_request: 'Other Request'
    };
    return labels[type] || type;
  };

  const toggleCardExpansion = (teacherId) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(teacherId)) {
      newExpandedCards.delete(teacherId);
    } else {
      newExpandedCards.add(teacherId);
    }
    setExpandedCards(newExpandedCards);
  };

  const toggleTeacherCardExpansion = (teacherId) => {
    const newExpandedTeacherCards = new Set(expandedTeacherCards);
    if (newExpandedTeacherCards.has(teacherId)) {
      newExpandedTeacherCards.delete(teacherId);
    } else {
      newExpandedTeacherCards.add(teacherId);
    }
    setExpandedTeacherCards(newExpandedTeacherCards);
  };

  // Filter teachers based on search term
  const filteredTeachers = teachers.filter(teacher =>
    (teacher.firstName + ' ' + teacher.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (teacher.qualification && teacher.qualification.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (teacher.specialization && teacher.specialization.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const tabButtonClass = (tab) => `px-5 py-2.5 text-sm font-semibold rounded-lg border transition-colors whitespace-nowrap ${
    activeTab === tab
      ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
      : 'bg-slate-50 text-slate-600 border-transparent hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-100'
  }`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading teachers management...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Teacher Management</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition duration-300"
            >
              Add Teacher
            </button>
            <DateTimeDisplay />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-xl shadow-sm p-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('teachers')}
            className={tabButtonClass('teachers')}
          >
            Teachers ({teachers.length})
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={tabButtonClass('assignments')}
          >
            Assignments
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={tabButtonClass('attendance')}
          >
            Attendance
          </button>
          <button
            onClick={() => setActiveTab('salaries')}
            className={tabButtonClass('salaries')}
          >
            Salaries
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={tabButtonClass('feedback')}
          >
            Feedback ({feedbackRecords.length})
          </button>
        </div>

        {/* Teachers Tab */}
        {activeTab === 'teachers' && (
          <div>
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search teachers by name, email, employee ID, qualification, or specialization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                />
              </div>
              {searchTerm && (
                <p className="text-sm text-gray-600 mt-2">
                  {teachers.filter(teacher =>
                    (teacher.firstName + ' ' + teacher.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
                    teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    teacher.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (teacher.qualification && teacher.qualification.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (teacher.specialization && teacher.specialization.toLowerCase().includes(searchTerm.toLowerCase()))
                  ).length} of {teachers.length} teachers match your search
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeachers.map((teacher) => {
              const isExpanded = expandedTeacherCards.has(teacher.id);

              return (
                <div key={teacher.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Collapsed Header - Clickable */}
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => toggleTeacherCardExpansion(teacher.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {/* Profile Picture */}
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-200 shadow-sm">
                          <img
                            src={teacher.profilePicture || '/fallback-data/default-avatar.png'}
                            alt={`${teacher.firstName} ${teacher.lastName}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/fallback-data/default-avatar.png';
                            }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-gray-800">
                              {teacher.firstName} {teacher.lastName}
                            </h3>
                            {/* Feedback Notification Icon */}
                            {(() => {
                              const teacherFeedback = feedbackRecords.filter(r => r.teacherId === teacher.id);
                              const pendingRequests = teacherFeedback.filter(r => r.status === 'pending').length;
                              return pendingRequests > 0 ? (
                                <div className="relative">
                                  <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                  </svg>
                                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center">
                                    {pendingRequests}
                                  </span>
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <p className="text-sm text-gray-600">{teacher.employeeId}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          teacher.employmentStatus === 'Full-time' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {teacher.employmentStatus}
                        </span>
                        {/* Expand/Collapse Icon */}
                        <svg
                          className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content */}
                  <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="space-y-4 pt-4">
                        <div className="grid grid-cols-1 gap-3 text-sm">
                          <div className="flex justify-between items-center py-1">
                            <span className="font-medium text-gray-600">Email:</span>
                            <span className="text-gray-800">{teacher.email}</span>
                          </div>
                          <div className="flex justify-between items-center py-1">
                            <span className="font-medium text-gray-600">Phone:</span>
                            <span className="text-gray-800">{teacher.phone}</span>
                          </div>
                          {teacher.qualification && (
                            <div className="flex justify-between items-center py-1">
                              <span className="font-medium text-gray-600">Qualification:</span>
                              <span className="text-gray-800">{teacher.qualification}</span>
                            </div>
                          )}
                          {teacher.specialization && (
                            <div className="flex justify-between items-center py-1">
                              <span className="font-medium text-gray-600">Specialization:</span>
                              <span className="text-gray-800">{teacher.specialization}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-1">
                            <span className="font-medium text-gray-600">Experience:</span>
                            <span className="text-gray-800">{teacher.experience} years</span>
                          </div>
                        </div>

                        <div className="p-3 bg-blue-50 rounded border border-blue-200">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">Login Credentials</h4>
                          <div className="text-xs text-blue-700 space-y-1">
                            <p><strong>Email:</strong> {teacher.email}</p>
                            <p><strong>Password:</strong> teacher123</p>
                            <p className="text-blue-600 italic">Default password - should be changed on first login</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditTeacher({
                                ...teacher,
                                bankAccount: {
                                  accountName: teacher.bankAccount?.accountName || '',
                                  bankName: teacher.bankAccount?.bankName || '',
                                  bankCode: teacher.bankAccount?.bankCode || teacher.bankAccount?.code || '',
                                  bankId: teacher.bankAccount?.bankId || '',
                                  accountNumber: teacher.bankAccount?.accountNumber || '',
                                  verified: Boolean(teacher.bankAccount?.verified),
                                  verifiedAt: teacher.bankAccount?.verifiedAt || ''
                                }
                              });
                              setShowEditModal(true);
                            }}
                            className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                          >
                            Edit Profile
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTeacher(teacher);
                              setShowAttendanceModal(true);
                            }}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                          >
                            Mark Attendance
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTeacher(teacher);
                              setShowSalaryModal(true);
                            }}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                          >
                            Add Salary
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResetPassword(teacher);
                            }}
                            className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTeacher(teacher);
                              setShowFeedbackModal(true);
                            }}
                            className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                          >
                            Send Feedback
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTeacher(teacher.id);
                            }}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                          >
                            Delete Teacher
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="space-y-6">
            {teachers.map((teacher) => (
              <div key={teacher.id} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {teacher.firstName} {teacher.lastName} ({teacher.employeeId})
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Class Assignments */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Assigned Classes</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {teacher.assignedClasses.map((className) => (
                        <span key={className} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                          {className}
                        </span>
                      ))}
                    </div>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignClass(teacher.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="border rounded px-3 py-1 text-sm"
                    >
                      <option value="">Assign to class...</option>
                      {classes.filter(c => !teacher.assignedClasses.includes(c)).map((className) => (
                        <option key={className} value={className}>{className}</option>
                      ))}
                    </select>
                  </div>

                  {/* Subject Assignments */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Assigned Subjects</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {teacher.assignedSubjects.map((subject) => (
                        <span key={subject} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                          {subject}
                        </span>
                      ))}
                    </div>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignSubject(teacher.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="border rounded px-3 py-1 text-sm"
                    >
                      <option value="">Assign to subject...</option>
                      {subjects.filter(s => !teacher.assignedSubjects.includes(s.name)).map((subject) => (
                        <option key={subject.id} value={subject.name}>{subject.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            {/* Attendance Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Attendance Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {attendanceRecords.filter(r => r.date === new Date().toISOString().split('T')[0] && r.status === 'present').length}
                  </div>
                  <div className="text-sm text-gray-600">Present Today</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {attendanceRecords.filter(r => r.date === new Date().toISOString().split('T')[0] && r.status === 'absent').length}
                  </div>
                  <div className="text-sm text-gray-600">Absent Today</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {attendanceRecords.filter(r => r.location && r.location.withinRange).length}
                  </div>
                  <div className="text-sm text-gray-600">GPS Validated</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {attendanceRecords.filter(r => r.autoChecked).length}
                  </div>
                  <div className="text-sm text-gray-600">Auto Check-ins</div>
                </div>
              </div>
            </div>

            {/* Individual Teacher Attendance */}
            {teachers.map((teacher) => {
              const teacherAttendance = attendanceRecords.filter(r => r.teacherId === teacher.id);
              const todayAttendance = teacherAttendance.find(r => r.date === new Date().toISOString().split('T')[0]);
              const presentCount = teacherAttendance.filter(r => r.status === 'present').length;
              const totalDays = teacherAttendance.length;
              const attendanceRate = totalDays > 0 ? Math.round((presentCount / totalDays) * 100) : 0;

              return (
                <div key={teacher.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {teacher.firstName} {teacher.lastName}
                    </h3>
                    {/* Feedback Notification Icon */}
                    {(() => {
                      const teacherFeedback = feedbackRecords.filter(r => r.teacherId === teacher.id);
                      const pendingRequests = teacherFeedback.filter(r => r.status === 'pending').length;
                      return pendingRequests > 0 ? (
                        <div className="relative">
                          <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                            {pendingRequests}
                          </span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <p className="text-sm text-gray-600">{teacher.employeeId}</p>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Attendance Rate</div>
                      <div className={`text-lg font-bold ${attendanceRate >= 80 ? 'text-green-600' : attendanceRate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {attendanceRate}%
                      </div>
                    </div>
                  </div>

                  {/* Today's Attendance Status */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Today's Status</h4>
                    {todayAttendance ? (
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                          todayAttendance.status === 'present'
                            ? 'bg-green-100 text-green-800'
                            : todayAttendance.status === 'late'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {todayAttendance.status.charAt(0).toUpperCase() + todayAttendance.status.slice(1)}
                        </span>
                        {todayAttendance.checkInTime && (
                          <span className="text-sm text-gray-600">
                            Check-in: {todayAttendance.checkInTime}
                          </span>
                        )}
                        {todayAttendance.location && (
                          <span className={`text-xs px-2 py-1 rounded ${
                            todayAttendance.location.withinRange
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {todayAttendance.location.withinRange ? 'GPS ?' : 'GPS ?'}
                          </span>
                        )}
                        {todayAttendance.autoChecked && (
                          <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                            Auto
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">No attendance marked for today</span>
                    )}
                  </div>

                  {/* Recent Attendance Records */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Attendance ({teacherAttendance.length} records)</h4>
                    {teacherAttendance.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {teacherAttendance.slice(0, 10).map((record) => (
                          <div key={record.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <div className="flex items-center space-x-3">
                              <span className="text-gray-600">{record.date}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.status === 'present'
                                  ? 'bg-green-100 text-green-800'
                                  : record.status === 'late'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {record.status}
                              </span>
                              {record.checkInTime && (
                                <span className="text-gray-600 text-xs">
                                  {record.checkInTime}
                                  {record.checkOutTime && ` - ${record.checkOutTime}`}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {record.location && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  record.location.withinRange
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  GPS {record.location.withinRange ? '?' : '?'}
                                </span>
                              )}
                              {record.autoChecked && (
                                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                                  Auto
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No attendance records found</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Salaries Tab */}
        {activeTab === 'salaries' && (
          <div className="space-y-6">
            {/* Salary Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Salary Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    ₦{salaryRecords.reduce((sum, record) => sum + record.netSalary, 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Total Paid This Month</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {salaryRecords.filter(r => r.paymentStatus === 'paid').length}
                  </div>
                  <div className="text-sm text-gray-600">Paid Salaries</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {salaryRecords.filter(r => r.paymentStatus === 'pending').length}
                  </div>
                  <div className="text-sm text-gray-600">Pending Payments</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {salaryRecords.filter(r => r.paymentStatus === 'overdue').length}
                  </div>
                  <div className="text-sm text-gray-600">Overdue Payments</div>
                </div>
              </div>
            </div>

            {/* Individual Teacher Salaries */}
            {teachers.map((teacher) => {
              const teacherSalaries = salaryRecords.filter(r => r.teacherId === teacher.id);
              const latestSalary = teacherSalaries[0]; // Most recent salary record
              const isExpanded = expandedCards.has(teacher.id);

              return (
                <div key={teacher.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Card Header */}
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => toggleCardExpansion(teacher.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {teacher.firstName} {teacher.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{teacher.employeeId}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        {/* Collapsed View - Basic Info */}
                        {!isExpanded && (
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="text-center">
                              <div className="text-xs text-gray-500">Latest Salary</div>
                              <div className="font-semibold text-gray-900">
                                ₦{latestSalary?.netSalary?.toLocaleString() || 'N/A'}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-500">Status</div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                latestSalary?.paymentStatus === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : latestSalary?.paymentStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {latestSalary?.paymentStatus ? latestSalary.paymentStatus.charAt(0).toUpperCase() + latestSalary.paymentStatus.slice(1) : 'No Records'}
                              </span>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-gray-500">Records</div>
                              <div className="font-semibold text-blue-600">{teacherSalaries.length}</div>
                            </div>
                          </div>
                        )}
                        {/* Expand/Collapse Icon */}
                        <button
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCardExpansion(teacher.id);
                          }}
                        >
                          <svg
                            className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content */}
                  <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6 border-t border-gray-100">

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Salary Information */}
                    <div className="space-y-4">
                      <h4 className="text-md font-semibold text-gray-700">Latest Salary Information</h4>

                      {latestSalary ? (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Month:</span>
                            <span className="font-medium">{latestSalary.month}</span>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 bg-gray-50 rounded">
                              <div className="text-xs text-gray-500">Base Salary</div>
                              <div className="text-lg font-bold text-gray-900">
                                ₦{latestSalary.baseSalary.toLocaleString()}
                              </div>
                            </div>
                            <div className="p-3 bg-green-50 rounded">
                              <div className="text-xs text-gray-500">Total Allowances</div>
                              <div className="text-lg font-bold text-green-700">
                                ₦{latestSalary.totalAllowances.toLocaleString()}
                              </div>
                            </div>
                            <div className="p-3 bg-red-50 rounded">
                              <div className="text-xs text-gray-500">Total Deductions</div>
                              <div className="text-lg font-bold text-red-700">
                                ₦{latestSalary.totalDeductions.toLocaleString()}
                              </div>
                            </div>
                            <div className="p-3 bg-blue-50 rounded">
                              <div className="text-xs text-gray-500">Net Salary</div>
                              <div className="text-lg font-bold text-blue-700">
                                ₦{latestSalary.netSalary.toLocaleString()}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-4">
                            <div className={`px-3 py-1 rounded text-sm font-medium ${
                              latestSalary.paymentStatus === 'paid'
                                ? 'bg-green-100 text-green-800'
                                : latestSalary.paymentStatus === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {latestSalary.paymentStatus.charAt(0).toUpperCase() + latestSalary.paymentStatus.slice(1)}
                            </div>
                            {latestSalary.paymentDate && (
                              <span className="text-sm text-gray-600">
                                Paid: {new Date(latestSalary.paymentDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          {/* Detailed Breakdown */}
                          <div className="border-t pt-3">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Allowance Breakdown</h5>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>Housing: ₦{latestSalary.allowances?.housing?.toLocaleString() || 0}</div>
                              <div>Transport: ₦{latestSalary.allowances?.transport?.toLocaleString() || 0}</div>
                              <div>Medical: ₦{latestSalary.allowances?.medical?.toLocaleString() || 0}</div>
                            </div>
                          </div>

                          <div className="border-t pt-3">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Deduction Breakdown</h5>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>Tax: ₦{latestSalary.deductions?.tax?.toLocaleString() || 0}</div>
                              <div>Insurance: ₦{latestSalary.deductions?.insurance?.toLocaleString() || 0}</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>No salary records found</p>
                          <p className="text-sm">Add salary information using the "Add Salary" button</p>
                        </div>
                      )}
                    </div>

                    {/* Bank Account Information */}
                    <div className="space-y-4">
                      <h4 className="text-md font-semibold text-gray-700">Bank Account Details</h4>

                      {teacher.bankAccount && (teacher.bankAccount.accountName || teacher.bankAccount.accountNumber) ? (
                        <div className="space-y-3">
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Account Name:</span>
                                <button
                                  onClick={() => copyToClipboard(teacher.bankAccount.accountName)}
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                  Copy
                                </button>
                              </div>
                              <p className="text-gray-900 font-mono">{teacher.bankAccount.accountName}</p>
                            </div>

                            <div className="space-y-2 mt-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Bank Name:</span>
                                <button
                                  onClick={() => copyToClipboard(teacher.bankAccount.bankName)}
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                  Copy
                                </button>
                              </div>
                              <p className="text-gray-900">{teacher.bankAccount.bankName}</p>
                            </div>

                            <div className="space-y-2 mt-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Account Number:</span>
                                <button
                                  onClick={() => copyToClipboard(teacher.bankAccount.accountNumber)}
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                  Copy
                                </button>
                              </div>
                              <p className="text-gray-900 font-mono text-lg">{teacher.bankAccount.accountNumber}</p>
                            </div>
                          </div>

                          <div className="flex space-x-2">
                            <button
                              onClick={() => copyToClipboard(`${teacher.bankAccount.accountName}\n${teacher.bankAccount.accountNumber}\n${teacher.bankAccount.bankName}`)}
                              className="flex-1 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                            >
                              Copy All Details
                            </button>
                            <button
                              onClick={() => copyToClipboard(`${teacher.firstName} ${teacher.lastName} - ₦${latestSalary?.netSalary?.toLocaleString() || 0}\nAccount: ${teacher.bankAccount.accountNumber} (${teacher.bankAccount.bankName})`)}
                              className="flex-1 bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700"
                            >
                              Copy Payment Info
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                          <p>No bank account information</p>
                          <p className="text-sm">Update teacher profile to add bank details</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Salary History */}
                  {teacherSalaries.length > 1 && (
                    <div className="mt-6 border-t pt-4">
                      <h4 className="text-md font-semibold text-gray-700 mb-3">Salary History</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {teacherSalaries.slice(1, 6).map((record) => (
                          <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm">
                            <div className="flex items-center space-x-4">
                              <span className="font-medium text-gray-700">{record.month}</span>
                              <span className="text-gray-600">₦{record.netSalary.toLocaleString()}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.paymentStatus === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : record.paymentStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {record.paymentStatus}
                              </span>
                            </div>
                            {record.paymentDate && (
                              <span className="text-xs text-gray-500">
                                {new Date(record.paymentDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
                    <button
                      onClick={() => {
                        setSelectedTeacher(teacher);
                        setShowSalaryModal(true);
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                    >
                      Add Salary Record
                    </button>
                  </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            {/* Feedback Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Feedback & Requests Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {feedbackRecords.filter(r => r.type === 'performance_feedback').length}
                  </div>
                  <div className="text-sm text-gray-600">Performance Feedback</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {feedbackRecords.filter(r => r.type === 'leave_request').length}
                  </div>
                  <div className="text-sm text-gray-600">Leave Requests</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {feedbackRecords.filter(r => r.status === 'pending').length}
                  </div>
                  <div className="text-sm text-gray-600">Pending Review</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {feedbackRecords.filter(r => r.status === 'approved').length}
                  </div>
                  <div className="text-sm text-gray-600">Approved</div>
                </div>
              </div>
            </div>

            {/* Feedback Records */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">All Feedback & Requests</h3>
                <button
                  onClick={() => {
                    setSelectedTeacher(null);
                    setShowFeedbackModal(true);
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  Submit General Feedback
                </button>
              </div>

              <div className="space-y-4">
                {feedbackRecords.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No feedback or requests found</p>
                ) : (
                  feedbackRecords.map((record) => {
                    // Find teacher information if not included in record
                    const teacherInfo = record.teacherName && record.employeeId
                      ? { name: record.teacherName, employeeId: record.employeeId }
                      : record.teacherId
                        ? teachers.find(t => t.id === record.teacherId)
                        : null;

                    return (
                      <div key={record.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-bold text-gray-800">{record.title || getRequestTypeLabel(record.type)}</h4>
                              <span className={`px-2 py-1 rounded text-xs ${
                                record.type === 'performance_feedback' ? 'bg-blue-100 text-blue-800' :
                                record.type === 'leave_request' ? 'bg-green-100 text-green-800' :
                                record.type === 'equipment_request' ? 'bg-orange-100 text-orange-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {getRequestTypeLabel(record.type)}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs ${
                                record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                record.status === 'approved' ? 'bg-green-100 text-green-800' :
                                record.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {record.status.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-gray-600 mb-2">{record.message}</p>
                            <div className="text-sm text-gray-500">
                              <p><strong>Teacher:</strong> {teacherInfo ? `${teacherInfo.firstName || teacherInfo.name} ${teacherInfo.lastName || ''}`.trim() : 'Unknown Teacher'} ({teacherInfo?.employeeId || 'N/A'})</p>
                              <p><strong>Submitted:</strong> {new Date(record.createdAt).toLocaleString()}</p>
                              {record.submittedByName && (
                                <p><strong>By:</strong> {record.submittedByName} ({record.submittedByRole})</p>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          {record.status === 'pending' && (
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => {
                                  const comments = prompt('Approval comments (optional):');
                                  handleUpdateFeedbackStatus(record.id, 'approved', comments);
                                }}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  const comments = prompt('Reason for rejection:');
                                  if (comments !== null) {
                                    handleUpdateFeedbackStatus(record.id, 'rejected', comments);
                                  }
                                }}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Request Details */}
                        {record.requestDetails && (
                          <div className="mt-3 p-3 bg-gray-50 rounded">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Request Details:</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              {record.requestDetails.startDate && (
                                <p><strong>Start Date:</strong> {record.requestDetails.startDate}</p>
                              )}
                              {record.requestDetails.endDate && (
                                <p><strong>End Date:</strong> {record.requestDetails.endDate}</p>
                              )}
                              {record.requestDetails.reason && (
                                <p><strong>Reason:</strong> {record.requestDetails.reason}</p>
                              )}
                              {record.requestDetails.item && (
                                <p><strong>Item:</strong> {record.requestDetails.item}</p>
                              )}
                              {record.requestDetails.justification && (
                                <p><strong>Justification:</strong> {record.requestDetails.justification}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Review Comments */}
                        {record.adminComments && (
                          <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                            <p className="text-sm font-medium text-blue-700">Review Comments:</p>
                            <p className="text-sm text-blue-600">{record.adminComments}</p>
                            {record.reviewedAt && (
                              <p className="text-xs text-blue-500 mt-1">
                                Reviewed: {new Date(record.reviewedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Teacher Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Teacher</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleAddTeacher} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={newTeacher.firstName}
                      onChange={(e) => setNewTeacher({...newTeacher, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={newTeacher.lastName}
                      onChange={(e) => setNewTeacher({...newTeacher, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={newTeacher.email}
                      onChange={(e) => setNewTeacher({...newTeacher, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={newTeacher.phone}
                      onChange={(e) => setNewTeacher({...newTeacher, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={newTeacher.address}
                      onChange={(e) => setNewTeacher({...newTeacher, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={newTeacher.dateOfBirth}
                      onChange={(e) => setNewTeacher({...newTeacher, dateOfBirth: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      value={newTeacher.gender}
                      onChange={(e) => setNewTeacher({...newTeacher, gender: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
                    <input
                      type="text"
                      value={newTeacher.qualification}
                      onChange={(e) => setNewTeacher({...newTeacher, qualification: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                    <input
                      type="text"
                      value={newTeacher.specialization}
                      onChange={(e) => setNewTeacher({...newTeacher, specialization: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years)</label>
                    <input
                      type="number"
                      value={newTeacher.experience}
                      onChange={(e) => setNewTeacher({...newTeacher, experience: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={newTeacher.hireDate}
                      onChange={(e) => setNewTeacher({...newTeacher, hireDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                    <select
                      value={newTeacher.employmentStatus}
                      onChange={(e) => setNewTeacher({...newTeacher, employmentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input
                      type="number"
                      value={newTeacher.baseSalary}
                      onChange={(e) => setNewTeacher({...newTeacher, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <input
                      type="number"
                      value={newTeacher.allowances}
                      onChange={(e) => setNewTeacher({...newTeacher, allowances: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductions</label>
                    <input
                      type="number"
                      value={newTeacher.deductions}
                      onChange={(e) => setNewTeacher({...newTeacher, deductions: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Bank Account Details</h3>
                  <VerifiedBankAccountFields
                    value={newTeacher.bankAccount}
                    onChange={(bankAccount) => setNewTeacher({...newTeacher, bankAccount})}
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newTeacher.emergencyContact.name}
                        onChange={(e) => setNewTeacher({
                          ...newTeacher,
                          emergencyContact: {...newTeacher.emergencyContact, name: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <input
                        type="text"
                        value={newTeacher.emergencyContact.relationship}
                        onChange={(e) => setNewTeacher({
                          ...newTeacher,
                          emergencyContact: {...newTeacher.emergencyContact, relationship: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newTeacher.emergencyContact.phone}
                        onChange={(e) => setNewTeacher({
                          ...newTeacher,
                          emergencyContact: {...newTeacher.emergencyContact, phone: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Teacher
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Teacher Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Teacher Profile</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleEditTeacher} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={editTeacher.firstName}
                      onChange={(e) => setEditTeacher({...editTeacher, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={editTeacher.lastName}
                      onChange={(e) => setEditTeacher({...editTeacher, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={editTeacher.email}
                      onChange={(e) => setEditTeacher({...editTeacher, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={editTeacher.phone}
                      onChange={(e) => setEditTeacher({...editTeacher, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={editTeacher.address}
                      onChange={(e) => setEditTeacher({...editTeacher, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={editTeacher.dateOfBirth}
                      onChange={(e) => setEditTeacher({...editTeacher, dateOfBirth: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      value={editTeacher.gender}
                      onChange={(e) => setEditTeacher({...editTeacher, gender: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qualification</label>
                    <input
                      type="text"
                      value={editTeacher.qualification}
                      onChange={(e) => setEditTeacher({...editTeacher, qualification: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                    <input
                      type="text"
                      value={editTeacher.specialization}
                      onChange={(e) => setEditTeacher({...editTeacher, specialization: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years)</label>
                    <input
                      type="number"
                      value={editTeacher.experience}
                      onChange={(e) => setEditTeacher({...editTeacher, experience: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={editTeacher.hireDate}
                      onChange={(e) => setEditTeacher({...editTeacher, hireDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                    <select
                      value={editTeacher.employmentStatus}
                      onChange={(e) => setEditTeacher({...editTeacher, employmentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input
                      type="number"
                      value={editTeacher.baseSalary}
                      onChange={(e) => setEditTeacher({...editTeacher, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <input
                      type="number"
                      value={editTeacher.allowances}
                      onChange={(e) => setEditTeacher({...editTeacher, allowances: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductions</label>
                    <input
                      type="number"
                      value={editTeacher.deductions}
                      onChange={(e) => setEditTeacher({...editTeacher, deductions: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Bank Account Details</h3>
                  <VerifiedBankAccountFields
                    value={editTeacher.bankAccount}
                    onChange={(bankAccount) => setEditTeacher({...editTeacher, bankAccount})}
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Profile Picture</h3>
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-blue-200 shadow-lg">
                      <img
                        src={editTeacher.profilePicture || '/fallback-data/default-avatar.png'}
                        alt={`${editTeacher.firstName} ${editTeacher.lastName}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/fallback-data/default-avatar.png';
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Upload New Picture</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setProfilePictureFile(e.target.files[0])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max 2MB. JPG, PNG, GIF, WebP allowed.</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={editTeacher.emergencyContact?.name || ''}
                        onChange={(e) => setEditTeacher({
                          ...editTeacher,
                          emergencyContact: {...editTeacher.emergencyContact, name: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <input
                        type="text"
                        value={editTeacher.emergencyContact?.relationship || ''}
                        onChange={(e) => setEditTeacher({
                          ...editTeacher,
                          emergencyContact: {...editTeacher.emergencyContact, relationship: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={editTeacher.emergencyContact?.phone || ''}
                        onChange={(e) => setEditTeacher({
                          ...editTeacher,
                          emergencyContact: {...editTeacher.emergencyContact, phone: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Update Teacher
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {showAttendanceModal && selectedTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Mark Attendance - {selectedTeacher.firstName} {selectedTeacher.lastName}
                </h2>
                <button
                  onClick={() => setShowAttendanceModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleMarkAttendance} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={attendanceForm.date}
                    onChange={(e) => setAttendanceForm({...attendanceForm, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={attendanceForm.status}
                    onChange={(e) => setAttendanceForm({...attendanceForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                    <option value="half-day">Half Day</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                    <input
                      type="time"
                      value={attendanceForm.checkInTime}
                      onChange={(e) => setAttendanceForm({...attendanceForm, checkInTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                    <input
                      type="time"
                      value={attendanceForm.checkOutTime}
                      onChange={(e) => setAttendanceForm({...attendanceForm, checkOutTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={attendanceForm.notes}
                    onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAttendanceModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Mark Attendance
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Salary Modal */}
      {showSalaryModal && selectedTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Add Salary Record - {selectedTeacher.firstName} {selectedTeacher.lastName}
                </h2>
                <button
                  onClick={() => setShowSalaryModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleAddSalary} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <input
                      type="month"
                      value={salaryForm.month}
                      onChange={(e) => setSalaryForm({...salaryForm, month: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input
                      type="number"
                      value={salaryForm.baseSalary}
                      onChange={(e) => setSalaryForm({...salaryForm, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Allowances</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Housing</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.housing}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, housing: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transport</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.transport}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, transport: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Medical</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.medical}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, medical: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Deductions</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
                      <input
                        type="number"
                        value={salaryForm.deductions.tax}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          deductions: {...salaryForm.deductions, tax: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                      <input
                        type="number"
                        value={salaryForm.deductions.insurance}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          deductions: {...salaryForm.deductions, insurance: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <select
                      value={salaryForm.paymentStatus}
                      onChange={(e) => setSalaryForm({...salaryForm, paymentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                    <input
                      type="date"
                      value={salaryForm.paymentDate}
                      onChange={(e) => setSalaryForm({...salaryForm, paymentDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={salaryForm.notes}
                    onChange={(e) => setSalaryForm({...salaryForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowSalaryModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Add Salary Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedTeacher ? `Send Feedback/Request to ${selectedTeacher.firstName} ${selectedTeacher.lastName}` : 'Submit General Feedback'}
                </h2>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleSubmitFeedback} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={feedbackForm.type}
                    onChange={(e) => setFeedbackForm({...feedbackForm, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="performance_feedback">Performance Feedback</option>
                    <option value="leave_request">Leave Request</option>
                    <option value="equipment_request">Equipment Request</option>
                    <option value="maintenance_request">Maintenance Request</option>
                    <option value="other_request">Other Request</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={feedbackForm.title}
                    onChange={(e) => setFeedbackForm({...feedbackForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief title for this feedback/request"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={feedbackForm.priority}
                    onChange={(e) => setFeedbackForm({...feedbackForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    value={feedbackForm.message}
                    onChange={(e) => setFeedbackForm({...feedbackForm, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Detailed feedback or request description"
                    required
                  />
                </div>

                {/* Conditional fields based on request type */}
                {(feedbackForm.type === 'leave_request') && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Leave Request Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={feedbackForm.requestDetails.startDate}
                          onChange={(e) => setFeedbackForm({
                            ...feedbackForm,
                            requestDetails: {...feedbackForm.requestDetails, startDate: e.target.value}
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={feedbackForm.requestDetails.endDate}
                          onChange={(e) => setFeedbackForm({
                            ...feedbackForm,
                            requestDetails: {...feedbackForm.requestDetails, endDate: e.target.value}
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                      <select
                        value={feedbackForm.requestDetails.reason}
                        onChange={(e) => setFeedbackForm({
                          ...feedbackForm,
                          requestDetails: {...feedbackForm.requestDetails, reason: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select reason</option>
                        <option value="vacation">Vacation</option>
                        <option value="sick_leave">Sick Leave</option>
                        <option value="personal">Personal</option>
                        <option value="maternity">Maternity</option>
                        <option value="bereavement">Bereavement</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {(feedbackForm.type === 'equipment_request') && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Equipment Request Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Item Requested</label>
                      <input
                        type="text"
                        value={feedbackForm.requestDetails.item}
                        onChange={(e) => setFeedbackForm({
                          ...feedbackForm,
                          requestDetails: {...feedbackForm.requestDetails, item: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="What equipment do you need?"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
                      <textarea
                        value={feedbackForm.requestDetails.justification}
                        onChange={(e) => setFeedbackForm({
                          ...feedbackForm,
                          requestDetails: {...feedbackForm.requestDetails, justification: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Why do you need this equipment?"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowFeedbackModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Submit {getRequestTypeLabel(feedbackForm.type)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherManagement;
